import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

const categoryMeta = {
  aptitude: {
    label: 'Aptitude',
    icon: 'bx-brain',
    badge: 'bg-emerald-100 text-emerald-600 border border-emerald-200',
    card: 'from-emerald-500/15 to-teal-500/10 border-emerald-200'
  },
  verbal: {
    label: 'Verbal',
    icon: 'bx-message-rounded-detail',
    badge: 'bg-rose-100 text-rose-600 border border-rose-200',
    card: 'from-rose-500/15 to-fuchsia-500/10 border-rose-200'
  },
  problem_solving: {
    label: 'Problem Solving',
    icon: 'bx-bulb',
    badge: 'bg-amber-100 text-amber-600 border border-amber-200',
    card: 'from-amber-500/15 to-orange-500/10 border-amber-200'
  }
};

const levelLabel = (level) => `Level ${level || 1}`;

const Assessments = () => {
  const { user } = useAuth();
  const [category, setCategory] = useState('all');
  const [data, setData] = useState({ assessments: [], attempts: [] });
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  useEffect(() => {
    const fetchAssessments = async () => {
      setLoading(true);
      try {
        const response = await api.get('/student/assessments', {
          params: { category }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch assessments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [category]);

  const upcomingAssessments = data.assessments.filter((assessment) => {
    return !data.attempts.some((attempt) => attempt.assessment_id === assessment.id);
  });

  const categoryCards = useMemo(() => (
    ['aptitude', 'verbal', 'problem_solving'].map((key) => ({
      key,
      label: categoryMeta[key].label,
      score: user?.skill_scores?.[key] || 0,
      level: user?.skill_levels?.[key] || 1,
      attempts: data.attempts.filter((attempt) => attempt.category === key).length
    }))
  ), [data.attempts, user]);

  const filteredAttempts = useMemo(() => {
    return [...data.attempts].sort(
      (a, b) => new Date(b.assessment_date || b.attempted_at) - new Date(a.assessment_date || a.attempted_at)
    );
  }, [data.attempts]);

  if (loading) {
    return <div className="text-center py-12">Loading assessments...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <section className="rounded-3xl border border-blue-200 bg-gradient-to-r from-[#dbe8ff] via-[#edf4ff] to-[#f8fbff] p-6 text-slate-900 shadow-lg">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-blue-700">Verified Assessment Layer</p>
            <h1 className="mt-2 text-2xl font-bold md:text-3xl">Your college-tested skill record</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Monthly assessments stay as raw history, while your latest aptitude, verbal, and problem solving levels roll up into your student profile.
            </p>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-700">Visible assessments</p>
            <p className="mt-1 text-3xl font-semibold text-slate-900">{data.assessments.length}</p>
            <p className="text-sm text-slate-600">Matched to your college and year</p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {categoryCards.map((card) => {
          const meta = categoryMeta[card.key];
          return (
            <div key={card.key} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${meta.badge}`}>
                  <i className={`bx ${meta.icon} text-2xl`}></i>
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  {levelLabel(card.level)}
                </span>
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-500">{card.label}</p>
              <p className="mt-1 text-3xl font-bold text-slate-900">{card.score} / 100</p>
              <p className="mt-2 text-sm text-slate-500">{card.attempts} scored assessments</p>
            </div>
          );
        })}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Assessment history</h2>
            <p className="text-sm text-slate-500">Filter by dimension to inspect your assessment cards.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {['all', 'aptitude', 'verbal', 'problem_solving'].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setCategory(option)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  category === option
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {option === 'all' ? 'All' : categoryMeta[option].label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredAttempts.length === 0 ? (
            <div className="col-span-full rounded-2xl bg-slate-50 p-6 text-center text-slate-500">
              No assessment results yet for this filter.
            </div>
          ) : (
            filteredAttempts.map((attempt) => {
              const meta = categoryMeta[attempt.category];
              return (
                <button
                  key={attempt.id}
                  type="button"
                  onClick={() => setSelectedAttempt(attempt)}
                  className={`rounded-3xl border bg-gradient-to-br ${meta?.card || 'from-slate-100 to-white border-slate-200'} p-5 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${meta?.badge}`}>
                      <i className={`bx ${meta?.icon || 'bx-file'} text-2xl`}></i>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-600">
                      {new Date(attempt.assessment_date || attempt.attempted_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-900">{attempt.assessment_name}</h3>
                  <p className="mt-2 text-sm text-slate-500">Click to view your normalized score.</p>
                </button>
              );
            })
          )}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900">Upcoming for your cohort</h2>
          <p className="mt-1 text-sm text-slate-500">Published assessments you have not been scored in yet.</p>
          <div className="mt-4 space-y-3">
            {upcomingAssessments.length === 0 ? (
              <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">No pending assessments right now.</p>
            ) : (
              upcomingAssessments.slice(0, 6).map((assessment) => (
                <div key={assessment.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{assessment.name}</p>
                      <p className="text-sm text-slate-500">
                        {categoryMeta[assessment.category]?.label || assessment.category} • Weight {assessment.weightage}%
                      </p>
                    </div>
                    <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
                      Year {assessment.target_year}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900">How this page works</h2>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <p>Every uploaded college test stays stored as a separate record so your history and graphs remain trustworthy.</p>
            <p>Your home page uses the latest aggregate scores, while this page shows the underlying monthly evidence.</p>
            <p>Levels are derived from normalized scores out of 100, so different tests can still compare fairly.</p>
          </div>
        </div>
      </section>

      {selectedAttempt ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  {categoryMeta[selectedAttempt.category]?.label || selectedAttempt.category}
                </p>
                <h3 className="mt-2 text-2xl font-bold text-slate-900">{selectedAttempt.assessment_name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAttempt(null)}
                className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200"
              >
                <i className="bx bx-x text-xl"></i>
              </button>
            </div>

            <div className="mt-6 rounded-3xl bg-slate-50 p-5 text-center">
              <p className="text-sm font-medium text-slate-500">You scored</p>
              <p className="mt-2 text-4xl font-bold text-slate-900">{selectedAttempt.normalized_score} / 100</p>
              <p className="mt-2 text-sm text-slate-500">
                Raw marks: {selectedAttempt.marks_obtained}/{selectedAttempt.max_marks}
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-slate-500">Level</p>
                <p className="mt-1 font-semibold text-slate-900">{levelLabel(selectedAttempt.score_level)}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-slate-500">Weightage</p>
                <p className="mt-1 font-semibold text-slate-900">{selectedAttempt.weightage}%</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Assessments;
